import React, { useMemo } from 'react'
import { Paper } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import moment from '@e3dc-react/shell/Libs/moment'
import useModals from 'src/Hooks/useModals'
import { nullsLast } from '@e3dc-react/shell/Libs/SortAlgorithms'
import { formatDuration } from '@e3dc-react/shell/Datetime'
import { ChargingSessionModelV1, AuthenticationInfoV1 } from 'src/Libs/e-mobility/charging-sessions'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { TableComplete } from '@e3dc-react/shell/Elements/Table'
import { WallboxHardwareModel } from 'src/Libs/wallboxes/models'
import { WallboxCell } from './components/WallboxCell'
import { getDuplicateWallboxNamesV1 } from '../charging-sessions/utils/get-duplicate-wallbox-names-v1'

const useStyles = makeStyles()(theme => ({
  tableHeader: {
    textTransform: 'uppercase',
    whiteSpace: 'pre-line',
  },
  tableContainer: {
    paddingLeft: theme.spacing(6),
    paddingRight: theme.spacing(6),
    paddingTop: theme.spacing(2),
  },
  row: {
    '&.MuiTableRow-root': {
      td: {
        height: 58, // keep row height consistent to avoid table height changing on page change
      },
    },
  },
}))

interface ChargingSessionTableProps {
  chargingSessions: ChargingSessionModelV1[]
  wallboxes: WallboxHardwareModel[]
  setResultPage?: (page: number) => void
  resultPage?: number
}

const ChargingSessionTable: React.VFC<ChargingSessionTableProps> = ({
  chargingSessions,
  wallboxes,
  resultPage,
  setResultPage,
}): React.ReactElement => {
  const { t } = useCustomTranslation()
  const { openChargingSessionDetailsV1 } = useModals()
  const { classes } = useStyles()
  const duplicateWallboxNames = getDuplicateWallboxNamesV1(wallboxes)

  const header = useMemo(
    () => [
      {
        value: 'wallbox',
        label: t('emobility.chargingSession.wallbox'),
        sortFunction: (wallboxA: ChargingSessionModelV1['wallbox'], wallboxB: ChargingSessionModelV1['wallbox']) => {
          /* since wallboxes can share the same name, we must sort by two fields, wallbox name and wallbox id */
          return (wallboxA.name + wallboxA.id).localeCompare(wallboxB.name + wallboxB.id)
        },
        valueRender: (wallbox: ChargingSessionModelV1['wallbox']) => {
          const hasDuplicate = !!wallbox.name && duplicateWallboxNames.includes(wallbox.name)

          return <WallboxCell wallboxName={wallbox.name} wallboxId={wallbox.id} showId={hasDuplicate} />
        },
      },
      {
        value: 'status',
        valueRender: (status: string) => {
          return t(`emobility.chargingSession.chargingSessionStatus.${status}`)
        },
        label: t('emobility.chargingSession.status'),
        sortFunction: (a: string, b: string) => a.localeCompare(b),
      },
      {
        value: 'startAt',
        valueRender: (startAt: string | null) => (startAt ? moment(startAt).format(t('common.format.date')) : ''),
        label: t('emobility.chargingSession.startAt'),
        sortFunction: nullsLast((a: string | null, b: string | null) => (a && b ? a.localeCompare(b) : 0)),
      },
      {
        value: 'durationMilliseconds',
        valueRender: (durationMilliseconds: number) => {
          return formatDuration(durationMilliseconds)
        },
        label: t('emobility.chargingSession.duration'),
        sortFunction: (a: number, b: number) => {
          return a - b
        },
      },
      {
        value: 'authentication',
        valueRender: (auth: ChargingSessionModelV1['authentication']) => {
          return <AuthenticationInfoV1 authType={auth.type} rfid={auth.rfid} />
        },
        label: t('emobility.chargingSession.authentication'),
        sortFunction: (a: ChargingSessionModelV1['authentication'], b: ChargingSessionModelV1['authentication']) =>
          `${a.type}${a.rfid}}`?.localeCompare(`${b.type}${b.rfid}`),
      },
    ],
    [t, duplicateWallboxNames]
  )

  return (
    <Paper className={classes.tableContainer}>
      <TableComplete
        header={header}
        data={chargingSessions}
        bodyElevation={0}
        footerElevation={0}
        headerElevation={0}
        rowClassName={classes.row}
        headerClassName={classes.tableHeader}
        coloredColumn
        initialSort={{
          name: 'startAt',
          direction: 'desc',
        }}
        onSelect={row => {
          const session = chargingSessions.find(session => session.id === row.id)

          if (!session) {
            return
          }
          openChargingSessionDetailsV1({
            session,
            wallboxes,
          })
        }}
        setResultPage={setResultPage}
        resultPage={resultPage}
      />
    </Paper>
  )
}

export default ChargingSessionTable
